const BASE = import.meta.env.VITE_API_URL ?? 'http://localhost:8787';

/**
 * Błąd z API, znormalizowany do jednego kształtu.
 *
 * Backend mówi dwoma głosami: nasze trasy zwracają `{ error: {...} }`,
 * a better-auth pod /api/auth zwraca `{ message, code }`. Reszta aplikacji
 * nie powinna o tym wiedzieć.
 */
export class ApiError extends Error {
  constructor(status, code, message, details = null) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.details = details;
  }
}

/**
 * Komunikaty better-auth przychodzą po angielsku. Tłumaczymy je tutaj,
 * a nie w widokach, żeby ten sam błąd brzmiał wszędzie tak samo.
 */
const MESSAGES = {
  INVALID_EMAIL_OR_PASSWORD: 'Nieprawidłowy e-mail lub hasło.',
  USER_ALREADY_EXISTS_USE_ANOTHER_EMAIL: 'Konto z tym adresem już istnieje.',
  USER_ALREADY_EXISTS: 'Konto z tym adresem już istnieje.',
  PASSWORD_TOO_SHORT: 'Hasło musi mieć co najmniej 10 znaków.',
  PASSWORD_TOO_LONG: 'Hasło jest za długie.',
  INVALID_EMAIL: 'To nie wygląda na poprawny adres e-mail.',
  EMAIL_NOT_VERIFIED: 'Potwierdź adres e-mail linkiem, który wysłaliśmy.',
  FIELD_NOT_ALLOWED: 'Tego pola nie można ustawić.',
  MISSING_OR_NULL_ORIGIN:
    'Żądanie odrzucone z powodów bezpieczeństwa. Sprawdź, czy adres API zgadza się z konfiguracją.',
  UNAUTHENTICATED: 'Ta operacja wymaga zalogowania.',
  FORBIDDEN: 'Nie masz uprawnień do tej operacji.',
  ACCOUNT_BANNED: 'To konto zostało zablokowane.',
  RATE_LIMITED: 'Za dużo prób. Odczekaj chwilę i spróbuj ponownie.',
  INTERNAL_ERROR: 'Coś poszło nie tak po naszej stronie. Spróbuj ponownie.',
};

/** @param {{code?: string, message?: string}} source */
function messageFor(source) {
  return MESSAGES[source.code] ?? source.message ?? 'Nie udało się wykonać operacji.';
}

async function send(url, { method = 'GET', body, signal, headers } = {}) {
  let response;

  try {
    response = await fetch(url, {
      method,
      // Bez tego ciasteczko sesji nie poleci do API na innym porcie/domenie.
      credentials: 'include',
      headers: {
        ...(body instanceof FormData ? {} : { 'content-type': 'application/json' }),
        ...headers,
      },
      body: body instanceof FormData ? body : body && JSON.stringify(body),
      signal,
    });
  } catch (err) {
    if (err.name === 'AbortError') throw err;
    throw new ApiError(0, 'NETWORK_ERROR', 'Brak połączenia z serwerem.');
  }

  if (response.status === 204) return null;

  const text = await response.text();
  let data;
  try {
    data = text ? JSON.parse(text) : null;
  } catch {
    throw new ApiError(
      response.status,
      'BAD_RESPONSE',
      'Serwer zwrócił nieczytelną odpowiedź.',
    );
  }

  if (!response.ok) {
    // `{ error: {...} }` z naszych tras, `{ message, code }` z better-auth.
    const source = data?.error ?? data ?? {};
    throw new ApiError(
      response.status,
      source.code ?? 'UNKNOWN',
      messageFor(source),
      source.details ?? null,
    );
  }

  return data;
}

/** Trasy aplikacji pod /api/v1. */
export async function request(path, options) {
  try {
    return await send(`${BASE}/api/v1${path}`, options);
  } catch (err) {
    // 401 z NASZYCH tras znaczy "sesja wygasła". Ten sam kod z /api/auth
    // znaczy "złe hasło", więc rozgłaszamy zdarzenie tylko tutaj.
    if (err instanceof ApiError && err.status === 401) {
      window.dispatchEvent(new CustomEvent('qz:session-expired'));
    }
    throw err;
  }
}

/** Trasy better-auth pod /api/auth. */
export function authRequest(path, options) {
  return send(`${BASE}/api/auth${path}`, options);
}

/** Buduje query string, pomijając puste wartości. */
function queryString(params = {}) {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== null && value !== '') search.set(key, value);
  }
  const text = search.toString();
  return text ? `?${text}` : '';
}

/**
 * Wysyłka pliku z postępem.
 *
 * `fetch` nie raportuje postępu wysyłania — strumieniowe ciała żądań wciąż
 * nie są powszechnie dostępne. XMLHttpRequest to jedyna droga do paska
 * postępu, więc ta jedna funkcja świadomie odstaje od reszty modułu.
 *
 * @param {FormData} formData
 * @param {(percent: number) => void} [onProgress]
 */
export function upload(path, formData, onProgress) {
  return new Promise((resolve, reject) => {
    const request = new XMLHttpRequest();
    request.open('POST', `${BASE}/api/v1${path}`);
    request.withCredentials = true; // odpowiednik credentials: 'include'

    request.upload.addEventListener('progress', (event) => {
      if (event.lengthComputable) onProgress?.(Math.round((event.loaded / event.total) * 100));
    });

    request.addEventListener('load', () => {
      let data;
      try {
        data = request.responseText ? JSON.parse(request.responseText) : null;
      } catch {
        reject(
          new ApiError(request.status, 'BAD_RESPONSE', 'Serwer zwrócił nieczytelną odpowiedź.'),
        );
        return;
      }

      if (request.status >= 200 && request.status < 300) {
        resolve(data);
        return;
      }

      const source = data?.error ?? data ?? {};
      reject(
        new ApiError(
          request.status,
          source.code ?? 'UNKNOWN',
          messageFor(source),
          source.details ?? null,
        ),
      );
    });

    request.addEventListener('error', () =>
      reject(new ApiError(0, 'NETWORK_ERROR', 'Brak połączenia z serwerem.')),
    );
    request.addEventListener('abort', () =>
      reject(new ApiError(0, 'ABORTED', 'Wysyłanie przerwane.')),
    );

    request.send(formData);
  });
}

/**
 * Adres obrazu do wstawienia w `<img src>`.
 *
 * Backend oddaje albo pełny adres publicznego koszyka R2, albo ścieżkę
 * `/api/v1/media/…` — a ta jest względna wobec **API**, nie wobec clienta.
 * Bez tego przeglądarka szukałaby pliku pod adresem strony i obraz pytania
 * nigdy by się nie pokazał: ani lokalnie (5173 vs 8787), ani na produkcji
 * (quizoteka.com vs api.quizoteka.com).
 *
 * @param {string|null|undefined} url
 */
export function mediaSrc(url) {
  if (!url) return null;
  return /^https?:\/\//.test(url) ? url : `${BASE}${url}`;
}

/**
 * Adres gniazda pojedynku.
 *
 * Uwierzytelnienie idzie ciasteczkiem sesji — przeglądarka dołącza je do
 * uścisku dłoni WebSocket tak samo jak do zwykłego żądania. Gdy klient i API
 * stoją na różnych domenach, ciasteczko musi mieć `SameSite=None`; to ta sama
 * konfiguracja (COOKIE_SAME_SITE), której wymaga już `credentials: 'include'`.
 */
export function duelSocketUrl(duelId) {
  const url = new URL(`${BASE}/api/v1/duels/${duelId}/ws`);
  url.protocol = url.protocol === 'https:' ? 'wss:' : 'ws:';
  return url.toString();
}

export const api = {
  me: (signal) => request('/me', { signal }),
  health: (signal) => request('/health', { signal }),

  categories: {
    list: (signal) => request('/categories', { signal }),
    create: (data) => request('/categories', { method: 'POST', body: data }),
    update: (id, data) => request(`/categories/${id}`, { method: 'PATCH', body: data }),
    remove: (id) => request(`/categories/${id}`, { method: 'DELETE' }),
  },

  admin: {
    overview: (signal) => request('/admin/overview', { signal }),

    /**
     * Ustawienia botów. Przełącznik `enabled` jest wyłącznikiem awaryjnym —
     * działa od razu, bez wdrożenia.
     */
    botSettings: {
      get: (signal) => request('/admin/settings/bots', { signal }),
      update: (patch) => request('/admin/settings/bots', { method: 'PATCH', body: patch }),
    },

    /**
     * Boty. `remove` usuwa tylko bota bez historii — pozostałe wyłącza, żeby
     * graczom nie zniknęły pojedynki; odpowiedź mówi, co się stało.
     */
    bots: {
      list: (signal) => request('/admin/bots', { signal }),
      metrics: (signal) => request('/admin/bots/metrics', { signal }),
      create: (data) => request('/admin/bots', { method: 'POST', body: data }),
      update: (userId, patch) =>
        request(`/admin/bots/${userId}`, { method: 'PATCH', body: patch }),
      remove: (userId) => request(`/admin/bots/${userId}`, { method: 'DELETE' }),
    },

    /**
     * Konta obsługuje wtyczka `admin` z better-auth, więc te wywołania idą
     * pod `/api/auth`, a nie `/api/v1`. Nie piszemy własnych tras do rzeczy,
     * które biblioteka robi poprawnie — łącznie z hashowaniem haseł.
     */
    users: {
      list: (signal) => authRequest('/admin/list-users?limit=200', { signal }),
      create: (data) => authRequest('/admin/create-user', { method: 'POST', body: data }),
      setRole: (userId, role) =>
        authRequest('/admin/set-role', { method: 'POST', body: { userId, role } }),
      ban: (userId, banReason) =>
        authRequest('/admin/ban-user', { method: 'POST', body: { userId, banReason } }),
      unban: (userId) => authRequest('/admin/unban-user', { method: 'POST', body: { userId } }),
      setPassword: (userId, newPassword) =>
        authRequest('/admin/set-user-password', {
          method: 'POST',
          body: { userId, newPassword },
        }),
      update: (userId, data) =>
        authRequest('/admin/update-user', { method: 'POST', body: { userId, data } }),
    },
  },

  /**
   * Baza pytań — wyłącznie warsztat redakcji.
   *
   * Quizu nie ma od migracji 0015: pytanie jest bytem samodzielnym i samo
   * niesie kategorię, poziom, tagi i przełącznik „w losowaniu". Publicznego
   * listowania nie ma i mieć nie może — te trasy oddają poprawne odpowiedzi.
   */
  questions: {
    list: (params, signal) => request(`/questions${queryString(params)}`, { signal }),
    get: (id, signal) => request(`/questions/${id}`, { signal }),
    create: (data) => request('/questions', { method: 'POST', body: data }),
    update: (id, data) => request(`/questions/${id}`, { method: 'PATCH', body: data }),
    remove: (id) => request(`/questions/${id}`, { method: 'DELETE' }),
    duplicate: (id) => request(`/questions/${id}/duplicate`, { method: 'POST', body: {} }),

    /** Publikacja, wycofanie albo usunięcie zaznaczonych — jednym żądaniem. */
    bulk: (ids, action) =>
      request('/questions/bulk', { method: 'PATCH', body: { ids, action } }),

    /**
     * Import z wklejki JSON-a.
     *
     * Odpowiedź jest zawsze `200` z wynikiem pozycja po pozycji — także wtedy,
     * gdy połowa wklejki się nie nadaje. `400` leci wyłącznie za kształt samej
     * koperty (nie tablica, pusta, ponad limit).
     *
     * @param {{ items: object[], dryRun?: boolean, publish?: boolean }} payload
     */
    import: (payload) => request('/questions/import', { method: 'POST', body: payload }),
  },

  duels: {
    /**
     * Wejście do kolejki i pytanie „jest już ktoś?" — jedno żądanie.
     * `categoryId: null` znaczy „wszystkie kategorie".
     */
    findOpponent: ({ categoryId = null } = {}) =>
      request('/duels/queue', { method: 'POST', body: { categoryId } }),
    leaveQueue: () => request('/duels/queue', { method: 'DELETE' }),
    get: (duelId, signal) => request(`/duels/${duelId}`, { signal }),
    history: (params, signal) => request(`/me/duels${queryString(params)}`, { signal }),
  },

  friends: {
    /** Wydaje własny kod. Dopóki poprzedni żyje, wraca ten sam. */
    code: () => request('/friends/code', { method: 'POST', body: {} }),
    redeem: (code) => request('/friends/redeem', { method: 'POST', body: { code } }),
    list: (signal) => request('/friends', { signal }),
    remove: (userId) => request(`/friends/${userId}`, { method: 'DELETE' }),
    invite: (userId, { categoryId = null } = {}) =>
      request(`/friends/${userId}/invite`, { method: 'POST', body: { categoryId } }),
  },

  invitations: {
    /**
     * Zaproszenia w obie strony. Ta sama trasa odnotowuje obecność, więc
     * odpytywanie jej jest jednocześnie mówieniem „wciąż tu jestem".
     */
    list: (signal) => request('/invitations', { signal }),
    accept: (id) => request(`/invitations/${id}/accept`, { method: 'POST', body: {} }),
    decline: (id) => request(`/invitations/${id}/decline`, { method: 'POST', body: {} }),
  },

  stats: {
    me: (signal) => request('/me/stats', { signal }),
    /** Skuteczność pytania — wskazuje te, które psują zabawę. */
    question: (questionId, signal) => request(`/questions/${questionId}/stats`, { signal }),
    global: (signal) => request('/leaderboard', { signal }),
  },

  media: {
    /**
     * Jedyny obraz w aplikacji wisi przy pytaniu — okładki quizów zniknęły
     * razem z quizami, więc `kind` i `quizId` nie mają już czego opisywać.
     *
     * @param {{file: File, questionId: string}} input
     */
    upload(input, onProgress) {
      const form = new FormData();
      form.append('file', input.file);
      form.append('questionId', input.questionId);

      return upload('/media', form, onProgress);
    },
    remove: (key) => request(`/media/${key}`, { method: 'DELETE' }),
  },
};
